// components/PatientDataCard.jsx
import React from "react";
import { useLocalization } from "../../../../context/LocalizeContextProvider";
import CardStatus from "./CardStatus";
import PillItem from "../../PillItem";
import { formatOptionsToArray } from "../../../../utils/utils";
import { colorsAllergys, Gender } from "../../../../utils/constants";
import styles from "../ModalDetailReservationV2.module.css";
import Button from "../../Button";
import PanelUpdatedData from "../../PanelUpdatedData";
import moment from "moment";

const PatientDataCard = ({
  isAssesment = false,
  isActive = true,
  isPatientNew = false,
  dataDetailReservation = {},
  detailPatient = null,
  isVitalSign = false,
  onClickVitalSign = () => {},
  isHistoryPatient = false,
  onClickDetail = () => {},
}) => {
  const localize = useLocalization();

  // Determine patient data based on whether we have detailed patient info or just reservation data
  const patientData = detailPatient
    ? {
        sequenceNumber: dataDetailReservation.noSequence,
        satuSehatId: detailPatient.satuSehatPatientId,
        medicalRecord: detailPatient.medicalRecordNumber,
        identityNumber: `${detailPatient.identificationNumber} (${detailPatient.identificationType})`,
        name: detailPatient.name,
        dateOfBirth: detailPatient.dob,
        email: detailPatient.email,
        phoneNumber: detailPatient.phoneNumber,
        gender:
          Gender().find((value) => value.id === detailPatient.gender)?.label ||
          "-",
        allergies: detailPatient.allergy || [],
      }
    : {
        // For cases without detailed patient data (new reservations)
        sequenceNumber: dataDetailReservation.noSequence,
        satuSehatId: null,
        medicalRecord: null,
        identityNumber: dataDetailReservation.identityNumber,
        name: dataDetailReservation.name,
        dateOfBirth: dataDetailReservation.dateOfBirth,
        gender:
          Gender().find((value) => value.id === dataDetailReservation.gender)
            ?.label || "-",
        phoneNumber: dataDetailReservation.phoneNumber,
        allergies: [],
      };

  const contentClass = isAssesment
    ? `${styles.patientContent} ${styles.patientContentAssesment}`
    : isActive
    ? `${styles.patientContent} ${styles.patientContentInactive}`
    : `${styles.patientContent} ${styles.patientContentInactive}`;

  const fieldClass = isActive
    ? `${styles.patientField} ${styles.patientFieldInactive}`
    : `${styles.patientField} ${styles.patientFieldInactive}`;

  return (
    <div className={styles.patientCard}>
      <div className={styles.patientCardHeader}>
        <div className={styles.patientCardTitle}>
          <i className={`far fa-user ${styles.patientIcon}`} />
          <p className={styles.patientTitle}>
            {detailPatient
              ? localize.getText("patientData")
              : localize.getText("userData")}
          </p>
        </div>
        {dataDetailReservation.status && (
          <CardStatus
            label={dataDetailReservation.status?.label}
            color={dataDetailReservation.status?.color}
          />
        )}
      </div>
      {isAssesment && (
        <PanelUpdatedData
          updatedAt={
            detailPatient?.lastModifiedDate
              ? moment(detailPatient.lastModifiedDate).format(
                  "DD MMMM YYYY, HH:mm"
                )
              : moment().format("DD MMMM YYYY, HH:mm")
          }
          updatedBy={detailPatient?.lastModifiedBy || ""}
        />
      )}
      <div className={contentClass}>
        <div className="d-flex gap-1">
          <div className="d-flex flex-column gap-2">
            <p className={fieldClass}>{localize.getText("sequenceNumber")}</p>
            {detailPatient && (
              <>
                <p className={fieldClass}>
                  {localize.getText("satuSehat")} -{" "}
                  {localize.getText("patientId")}
                </p>
                <p className={fieldClass}>
                  {localize.getText("medicalRecord")}
                </p>
              </>
            )}
            <p className={fieldClass}>{localize.getText("identityNumber")}</p>
            {isVitalSign && (
              <Button
                label="Tanda Vital"
                size="sm"
                variant="success"
                leftIcon={<i className="fas fa-heart"></i>}
                onClick={onClickVitalSign}
              />
            )}
          </div>
          <div className="d-flex flex-column gap-2">
            <p className="m-0 font-w500">: {patientData.sequenceNumber}</p>
            {detailPatient && (
              <>
                <p className="m-0 font-w500">
                  : {patientData.satuSehatId || "-"}
                </p>
                <p className="m-0 font-w500">: {patientData.medicalRecord}</p>
              </>
            )}
            <p className="m-0 font-w500">: {patientData.identityNumber}</p>
          </div>
        </div>
        <div className="d-flex gap-1">
          <div className="d-flex flex-column gap-2">
            <p className={fieldClass}>{localize.getText("name")}</p>
            {detailPatient?.email && (
              <p className={fieldClass}>{localize.getText("email")}</p>
            )}
            {detailPatient?.phoneNumber && (
              <p className={fieldClass}>{localize.getText("phoneNumber")}</p>
            )}
            <p className={fieldClass}>{localize.getText("dateOfBirth")}</p>
            <p className={fieldClass}>{localize.getText("gender")}</p>
            {detailPatient && (
              <p className={fieldClass}>{localize.getText("alergies")}</p>
            )}
            {isHistoryPatient && (
              <p className={styles.detailLink} onClick={onClickDetail}>
                {localize.getText("history-visit")}
              </p>
            )}
          </div>
          <div className="d-flex flex-column gap-2">
            <div className={styles.patientName}>
              <p className="m-0 font-w500">: {patientData.name}</p>
              {isPatientNew && (
                <PillItem text={localize.getText("patientNew")} isSmall />
              )}
            </div>
            {detailPatient?.email && (
              <p className="m-0 font-w500">: {patientData.email}</p>
            )}
            {detailPatient?.phoneNumber && (
              <p className="m-0 font-w500">: {patientData.phoneNumber}</p>
            )}
            <p className="m-0 font-w500">: {patientData.dateOfBirth}</p>
            <p className="m-0 font-w500">: {patientData.gender}</p>
            {detailPatient && (
              <div className={styles.allergiesContainer}>
                <p className="m-0 font-w500">:</p>
                {formatOptionsToArray(patientData.allergies).length > 0
                  ? formatOptionsToArray(patientData.allergies).map(
                      (item, index) => (
                        <PillItem
                          text={item}
                          key={index}
                          bgColor={
                            colorsAllergys[index % colorsAllergys.length]
                          }
                        />
                      )
                    )
                  : "-"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDataCard;
